import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOffline } from '@/contexts/OfflineContext';
import { getFromOfflineStore } from '@/lib/offlineDb';
import { useEffect, useRef } from 'react';
import { createRealtimeChannelName, logRealtimeIssue, removeRealtimeChannel } from '@/lib/realtime';

export const useRooms = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isOnline, cacheData } = useOffline();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Real-time subscription for rooms
  useEffect(() => {
    if (!isOnline) return;

    const invalidate = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['rooms'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      }, 500);
    };

    try {
      const channel = supabase
        .channel(createRealtimeChannelName('rooms-realtime'))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'rooms' }, invalidate)
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Rooms', error);
        });

      return () => {
        clearTimeout(debounceRef.current);
        removeRealtimeChannel(channel);
      };
    } catch (error) {
      logRealtimeIssue('Rooms', error);
    }
  }, [queryClient, isOnline]);

  const { data: rooms = [], isLoading } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      if (!isOnline) {
        console.log('Offline: Loading rooms from cache');
        return getFromOfflineStore<any>('rooms');
      }

      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .order('room_number', { ascending: true });

      if (error) throw error;
      
      // Cache data for offline use
      if (data) {
        await cacheData('rooms', data);
      }
      
      return data || [];
    },
    staleTime: isOnline ? 30000 : Infinity
  });

  const createRoom = useMutation({
    mutationFn: async (roomData: {
      roomNumber: string;
      subRoom?: string;
      capacity?: number;
      siteId?: string;
      isHandicapAccessible?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('rooms')
        .insert([{
          site_id: roomData.siteId || '00000000-0000-0000-0000-000000000000',
          room_number: roomData.subRoom ? `${roomData.roomNumber}${roomData.subRoom}` : roomData.roomNumber,
          capacity: roomData.capacity || 1,
          is_occupied: false,
          is_handicap_accessible: roomData.isHandicapAccessible || false
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      toast({
        title: 'Success',
        description: 'Room created successfully'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create room'
      });
    }
  });

  const updateRoom = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; isAvailable?: boolean }) => {
      const { data, error } = await supabase
        .from('rooms')
        .update({
          is_occupied: !(updates.isAvailable ?? true)
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      toast({
        title: 'Success',
        description: 'Room updated successfully'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update room'
      });
    }
  });

  return {
    rooms,
    isLoading,
    createRoom: createRoom.mutate,
    updateRoom: updateRoom.mutate
  };
};
